import '../public-path';
import React from 'react';
import ReactDOM from 'react-dom';
import {
  documentReady,
  getBaseUrl,
  getDataAttribute,
  normalizeChatbotPosition,
  type ChatbotPosition,
  type ChatbotPositionInput,
} from '@omq/shared';
import { ChatContainer } from './components';
import { resolveChatbotDisplayMode, type ChatbotDisplayMode } from './components/displayMode';
import './chatbot.less';
// NOTE: codemirror.css is intentionally NOT imported here. It ships in the lazy
// "code-editor" chunk (code-editor.bundle.css) via @omq/answer-code-element,
// which is dynamically imported by initializeAnswerEditors only when an answer
// contains a code block. Importing it eagerly here would fold ~72 .CodeMirror*
// rules into chatbot.min.css (and thus into the customer-editable complete chat
// style) for no benefit, since the lazy chunk already loads the CSS on demand.

let chatbotRoot: HTMLElement | null = null;
let chatbotRenderTarget: HTMLElement | null = null;

const ChatbotConfigAttributeNames = {
  ACCOUNT: 'chatbot-account',
  APIKEY: 'chatbot-api-key',
  POSITION: 'chatbot-position',
  CUSTOM_VALUES: 'chatbot-custom-values',
  AUTO_OPEN: 'chatbot-auto-open',
  TEST_MODE: 'chatbot-test-mode',
  BUBBLE_TITLE: 'chatbot-bubble-title',
  DISPLAY_MODE: 'chatbot-display-mode',
};

// Set on the host element in embedded mode. chatbot.less keys the host's default
// height off it, so the snippet attribute and the JS config are handled alike.
const EMBEDDED_HOST_ATTRIBUTE = 'data-omq-chatbot-embedded';

export interface ChatbotCallbacks {
  onClose?: () => boolean | void;
  onOpen?: () => boolean | void;
  onEndSession?: () => boolean | void;
  onNavigate?: (url: string) => boolean | void;
}

export interface ChatbotConfig {
  account: string;
  apiKey: string;
  // Accepts canonical 'right' | 'center' | 'left' and legacy 'bottom-right' | 'bottom-left'.
  // When omitted, the value from the backend (/chat_bot/omq/init) is used.
  position?: ChatbotPositionInput;
  customValues?: Record<string, string | number | boolean>;
  autoOpen?: boolean;
  testMode?: boolean;
  // Native hover tooltip text for the collapsed bubble button. Snippet-driven only
  // (data-omq-chatbot-bubble-title or config.bubbleTitle). No backend/admin source.
  bubbleTitle?: string;
  // 'floating' (default) or 'embedded'. Snippet-driven only: embedding depends on
  // where the integrator placed #chatbot-container, so there is no admin setting.
  displayMode?: ChatbotDisplayMode;
  onClose?: () => boolean | void;
  onOpen?: () => boolean | void;
  onEndSession?: () => boolean | void;
  onNavigate?: (url: string) => boolean | void;
}

/**
 * Module-level callbacks that can be set after initialization.
 * Returning false from a callback prevents the default behavior.
 */
const chatbotCallbacks: ChatbotCallbacks = {};

/**
 * Module-level controls registered by ChatContainer on mount.
 * Allows external JavaScript to open, close, or toggle the chat window.
 */
export interface ChatbotControls {
  open: () => void;
  close: () => void;
  toggle: () => void;
}
const chatbotControls: Partial<ChatbotControls> = {};

/**
 * Initialize chatbot client
 */
function initializeChatbot(config?: Partial<ChatbotConfig>) {
  try {
    // Find the target element
    const element = document.getElementById('chatbot-container');

    if (!element) {
      throw new Error('Target element with id "chatbot-container" not found');
    }

    chatbotRoot = element;

    // Get config from data attributes or passed config
    const account =
      config?.account || getDataAttribute(element, ChatbotConfigAttributeNames.ACCOUNT);
    const apiKey = config?.apiKey || getDataAttribute(element, ChatbotConfigAttributeNames.APIKEY);
    // Capture user-supplied position from config or data-attribute (highest priority).
    // If neither is set, ChatContainer will fall back to the backend value, then to 'right'.
    const rawExplicitPosition =
      config?.position ?? getDataAttribute(element, ChatbotConfigAttributeNames.POSITION);
    const explicitPosition: ChatbotPosition | undefined =
      normalizeChatbotPosition(rawExplicitPosition);

    // Parse customValues from data attribute (JSON string) or config object
    let customValues: Record<string, string | number | boolean> | undefined;
    if (config?.customValues) {
      customValues = config.customValues;
    } else {
      const customValuesAttr = getDataAttribute(element, ChatbotConfigAttributeNames.CUSTOM_VALUES);
      if (customValuesAttr) {
        try {
          customValues = JSON.parse(customValuesAttr);
        } catch (error) {
          console.error('[Chatbot] Failed to parse customValues:', error);
        }
      }
    }

    // Parse autoOpen from data attribute or config
    const autoOpen =
      config?.autoOpen ??
      getDataAttribute(element, ChatbotConfigAttributeNames.AUTO_OPEN) === 'true';

    // Parse testMode from data attribute or config
    const testMode =
      config?.testMode ??
      getDataAttribute(element, ChatbotConfigAttributeNames.TEST_MODE) === 'true';

    // Native hover tooltip for the collapsed bubble button. Empty string -> undefined
    // so the button renders no title attribute.
    const bubbleTitleRaw =
      config?.bubbleTitle ?? getDataAttribute(element, ChatbotConfigAttributeNames.BUBBLE_TITLE);
    const bubbleTitle =
      bubbleTitleRaw && bubbleTitleRaw.trim().length > 0 ? bubbleTitleRaw : undefined;

    const displayMode: ChatbotDisplayMode = resolveChatbotDisplayMode(
      config?.displayMode,
      getDataAttribute(element, ChatbotConfigAttributeNames.DISPLAY_MODE),
    );

    // Set callbacks from config
    if (config?.onClose) {
      chatbotCallbacks.onClose = config.onClose;
    }
    if (config?.onOpen) {
      chatbotCallbacks.onOpen = config.onOpen;
    }
    if (config?.onEndSession) {
      chatbotCallbacks.onEndSession = config.onEndSession;
    }
    if (config?.onNavigate) {
      chatbotCallbacks.onNavigate = config.onNavigate;
    }

    if (!account || !apiKey) {
      throw new Error('Both account and apiKey are required');
    }

    // Toggled (not only set) so a re-initialization in floating mode drops it again.
    element.toggleAttribute(EMBEDDED_HOST_ATTRIBUTE, displayMode === 'embedded');

    // Create Shadow DOM for style isolation
    const shadow = element.shadowRoot || element.attachShadow({ mode: 'open' });

    // Load base styles into the shadow root (isolated from host page)
    loadBaseStyleSheet(shadow, account);

    // Create render target inside shadow root
    let renderTarget = shadow.querySelector('#omq-chatbot-root') as HTMLElement | null;
    if (!renderTarget) {
      renderTarget = document.createElement('div');
      renderTarget.id = 'omq-chatbot-root';
      shadow.appendChild(renderTarget);
    }
    chatbotRenderTarget = renderTarget;

    // Render the React app inside shadow DOM
    ReactDOM.render(
      // Keyed by mode: the open/closed state is only initialised on mount, so a
      // re-initialization that switches the mode must remount the widget.
      <ChatContainer
        key={displayMode}
        account={account}
        apiKey={apiKey}
        explicitPosition={explicitPosition}
        customValues={customValues}
        autoOpen={autoOpen}
        testMode={testMode}
        bubbleTitle={bubbleTitle}
        displayMode={displayMode}
        callbacks={chatbotCallbacks}
        controls={chatbotControls}
        shadowRoot={shadow}
      />,
      renderTarget,
    );
  } catch (error) {
    console.error('[Chatbot] Failed to initialize chatbot:', error);
  }
}

const loadBaseStyleSheet = (shadowRoot: ShadowRoot, account: string) => {
  // Skip if styles already loaded in this shadow root
  if (shadowRoot.querySelector('#omq-chatbot-stylesheet')) {
    return;
  }

  // Load base CSS into shadow root
  const baseLink = document.createElement('link');
  baseLink.href = `${getBaseUrl(account) || ''}/chatbot/chatbot.min.css`;
  baseLink.rel = 'stylesheet';
  baseLink.id = 'omq-chatbot-stylesheet';
  shadowRoot.appendChild(baseLink);
};

/**
 * Destroys the chatbot instance
 */
function destroyChatbot(): void {
  if (chatbotRenderTarget) {
    ReactDOM.unmountComponentAtNode(chatbotRenderTarget);
    chatbotRenderTarget = null;
  }
  // Otherwise the emptied host keeps its embedded default height as blank space.
  chatbotRoot?.removeAttribute(EMBEDDED_HOST_ATTRIBUTE);
  chatbotRoot = null;
}

/**
 * Set a callback for when the close button is clicked.
 * Return false to prevent the default close behavior.
 */
function setOnClose(callback: () => boolean | void): void {
  chatbotCallbacks.onClose = callback;
}

/**
 * Set a callback for when the chat window is opened.
 * Return false to prevent the default open behavior.
 */
function setOnOpen(callback: () => boolean | void): void {
  chatbotCallbacks.onOpen = callback;
}

/**
 * Set a callback for when the end chat option is selected.
 * Return false to prevent the default end session behavior.
 */
function setOnEndSession(callback: () => boolean | void): void {
  chatbotCallbacks.onEndSession = callback;
}

/**
 * Set a callback for when a navigate event is triggered.
 * The callback receives the URL to navigate to.
 * Return false to prevent the default navigation behavior.
 */
function setOnNavigate(callback: (url: string) => boolean | void): void {
  chatbotCallbacks.onNavigate = callback;
}

/**
 * Open the chat window programmatically. No-op in embedded mode (always open).
 */
function open(): void {
  chatbotControls.open?.();
}

/**
 * Close the chat window programmatically. No-op in embedded mode (always open).
 */
function close(): void {
  chatbotControls.close?.();
}

/**
 * Toggle the chat window programmatically. No-op in embedded mode (always open).
 */
function toggle(): void {
  chatbotControls.toggle?.();
}

// Expose public API on window
(window as any).OMQChatbot = {
  initializeChatbot,
  destroyChatbot,
  open,
  close,
  toggle,
  setOnClose,
  setOnOpen,
  setOnEndSession,
  setOnNavigate,
};

// Initialize when document is ready, unless the page already initialized the
// chatbot itself: re-running it would reset that call's config to the attributes.
documentReady().then(() => {
  if (!chatbotRenderTarget) {
    initializeChatbot();
  }
});

// Export for potential external use
export { initializeChatbot, destroyChatbot, open, close, toggle, setOnClose, setOnOpen, setOnEndSession, setOnNavigate };
